import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";

/*
 * Admin Panel — Prep Failures dashboard.
 *
 * Read-only observability view over three unhealthy states that can
 * happen during the pre-interview background LLM generation phase:
 *   - prep_failed:        the background task raised.
 *   - stuck_preparing:    the task never advanced past `preparing` (>5m).
 *   - limited_validation: interview shipped but the semantic audit LLM
 *                         was unreachable — deterministic guards only.
 *
 * Backed by GET /api/admin/prep-failures. Refresh is manual, no polling —
 * ops opens this page when investigating an outage and clicks refresh.
 * Retry / refund actions are intentionally NOT wired here; the credit
 * ledger + retry model will be defined separately before Beta testing.
 */

function fmt(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function fmtRel(v) {
  if (!v) return "—";
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return String(v);
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

const KIND_STYLES = {
  prep_failed:        { label: "Prep failed",       cls: "bg-rose-50 text-rose-700 border-rose-200" },
  stuck_preparing:    { label: "Stuck preparing",   cls: "bg-amber-50 text-amber-800 border-amber-200" },
  limited_validation: { label: "Limited validation", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  unknown:            { label: "Unknown",           cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function AdminPrepFailures() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ hard: 0, limited: 0, combined: 0 });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/prep-failures", { params: { limit: 500 } });
      setRows(r.data.entries || []);
      setTotals({
        hard: r.data.hard_failures_last_24h ?? 0,
        limited: r.data.limited_validation_last_24h ?? 0,
        combined: r.data.failures_last_24h ?? 0,
      });
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-prep-failures">
      <AdminNav title="Prep Failures" />

      <main className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span
              className="rounded-full border border-rose-200 bg-rose-50 text-rose-700 px-3 py-1"
              data-testid="prep-badge-hard"
            >
              Hard failures (24h): <strong className="ml-1">{totals.hard}</strong>
            </span>
            <span
              className="rounded-full border border-sky-200 bg-sky-50 text-sky-700 px-3 py-1"
              data-testid="prep-badge-limited"
            >
              Limited validation (24h): <strong className="ml-1">{totals.limited}</strong>
            </span>
            <span
              className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-[color:var(--text-secondary)]"
              data-testid="prep-badge-total"
            >
              Total shown: <strong className="ml-1">{rows.length}</strong>
            </span>
          </div>
          <button
            type="button"
            onClick={refresh}
            className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-xs hover:bg-[color:var(--bg-surface-hover)]"
            data-testid="admin-prep-failures-refresh"
          >
            Refresh
          </button>
        </div>

        <div className="rounded-2xl border border-[color:var(--border-stone)] bg-white overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-[color:var(--text-secondary)]" data-testid="admin-prep-failures-loading">
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-[color:var(--text-secondary)]" data-testid="admin-prep-failures-empty">
              No preparation failures. Everything is healthy right now.
            </div>
          ) : (
            <PrepFailuresTable rows={rows} />
          )}
        </div>

        <p className="text-[11px] text-[color:var(--text-secondary)]">
          Read-only view of sessions that failed or degraded during LLM
          preparation. Retry and credit-refund controls will be added once
          the credit-ledger model for failed sessions is finalised.
        </p>
      </main>
    </div>
  );
}

function PrepFailuresTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="admin-prep-failures-table">
        <thead className="bg-[color:var(--bg-surface-hover)] text-[color:var(--text-secondary)] uppercase tracking-widest">
          <tr>
            <th className="text-left px-3 py-2">When</th>
            <th className="text-left px-3 py-2">Failure</th>
            <th className="text-left px-3 py-2">User</th>
            <th className="text-left px-3 py-2">Session</th>
            <th className="text-left px-3 py-2">Format</th>
            <th className="text-left px-3 py-2">Prep stage</th>
            <th className="text-left px-3 py-2">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const kind = KIND_STYLES[r.failure_kind] || KIND_STYLES.unknown;
            const detail =
              r.failure_kind === "limited_validation"
                ? r.validation_status
                : r.prep_error || r.status;
            return (
              <tr
                key={r.session_id}
                className="border-t border-[color:var(--border-stone)] align-top"
                data-testid={`prep-failure-row-${r.session_id}`}
              >
                <td className="px-3 py-2 whitespace-nowrap">
                  <div>{fmt(r.created_at)}</div>
                  <div className="text-[10px] text-[color:var(--text-secondary)]">{fmtRel(r.created_at)}</div>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${kind.cls}`}
                    data-testid={`prep-failure-kind-${r.session_id}`}
                  >
                    {kind.label}
                  </span>
                </td>
                <td className="px-3 py-2 break-all">
                  <div className="text-[color:var(--text-primary)]">{r.user_first_name || "—"}</div>
                  <div className="text-[10px] text-[color:var(--text-secondary)]">{r.user_email || r.user_id || "—"}</div>
                </td>
                <td className="px-3 py-2 font-mono text-[10px] break-all">{r.session_id}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.practice_format || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.prep_stage || "—"}</td>
                <td className="px-3 py-2 break-all">{detail || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
